/**
 * assets/privada-cart-interceptor.js
 *
 * 1) Routes all cart-icon clicks to ReCharge's drawer instead of the
 *    theme's built-in cart. Accompanies privada-global.css which hides
 *    the theme cart visually; this script prevents it from ever trying
 *    to open in the first place.
 *
 * 2) Keeps the theme's header cart count (<span id="cart-count">) in
 *    sync after ANY cart mutation — including ReCharge's remove-from-
 *    drawer action which hits /cart/change.js and doesn't trigger the
 *    theme's native count-update logic.
 *
 * Cart icon identified from theme markup as:
 *   <a ... data-recharge-cart-trigger-observer ...>
 *
 * ReCharge's drawer-open API identified via:
 *   window.RechargeBundle.onAddToCart()  -> storefront-experiences.js
 *
 * Runs on every page; safe no-op if trigger element or RC bundle
 * isn't present (e.g. during theme inspector, in admin iframes, etc).
 */
(function () {
  'use strict';
  if (window.__PRIVADA_CART_INTERCEPTOR__) return;
  window.__PRIVADA_CART_INTERCEPTOR__ = true;

  /* ── Part 1: Cart icon click interception ─────────────── */

  /* Match both possible trigger attribute names so this keeps working
     if the theme is ever updated to use the canonical attribute. */
  const TRIGGER_SELECTOR =
    '[data-recharge-cart-trigger], [data-recharge-cart-trigger-observer]';

  function openRechargeDrawer() {
    if (
      window.RechargeBundle &&
      typeof window.RechargeBundle.onAddToCart === 'function'
    ) {
      try {
        window.RechargeBundle.onAddToCart();
        return true;
      } catch (e) {
        console.warn('[Privada] RechargeBundle.onAddToCart threw:', e);
      }
    }
    return false;
  }

  function handleCartClick(e) {
    /* Allow cmd/ctrl/middle/shift clicks to behave as normal link clicks
       so power users can still open /cart in a new tab if they want. */
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

    /* Block the theme's own handlers and native navigation */
    e.preventDefault();
    e.stopImmediatePropagation();

    const opened = openRechargeDrawer();
    if (!opened) {
      /* ReCharge not ready — fall back to the full cart page so the
         user always gets somewhere useful. */
      window.location.href = '/cart';
    }
  }

  /* Delegate to document so this works even for cart icons rendered
     after page load (e.g. in sticky headers that re-render on scroll). */
  document.addEventListener(
    'click',
    function (e) {
      const trigger = e.target.closest(TRIGGER_SELECTOR);
      if (trigger) handleCartClick(e);
    },
    true /* capture phase — fires before theme's listeners */
  );


  /* ── Part 2: Header cart-count sync ──────────────────── */

  /**
   * Fetch the real cart state and write the item_count into the theme's
   * <span id="cart-count"> badge. If the cart is empty, hides it or
   * writes "0" depending on whether the element has a hidden class.
   */
  async function syncCartCount() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const cart = await res.json();
      const count = typeof cart.item_count === 'number' ? cart.item_count : 0;

      const badge = document.querySelector('#cart-count');
      if (badge) badge.textContent = String(count);

      /* If the theme uses a count attribute or a parent "has-items"
         class, keep those in sync too for visual state (badge color,
         hidden-when-zero, etc). */
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.setAttribute('data-cart-count', String(count));
        el.textContent = String(count);
      });
    } catch (e) {
      /* Silent — if the sync fails, stale count for a moment isn't
         critical. It'll correct itself on next mutation. */
    }
  }

  /* Cart-mutation URL patterns we care about. Any fetch/XHR to one of
     these means the cart changed, so we re-sync the count afterward. */
  const CART_MUTATION_PATHS = [
    '/cart/add',
    '/cart/change',
    '/cart/update',
    '/cart/clear',
  ];

  function isCartMutationUrl(url) {
    if (!url) return false;
    /* Accept full URLs, relative paths, and query strings */
    const path = String(url).split('?')[0];
    return CART_MUTATION_PATHS.some(p => path.endsWith(p + '.js') || path.endsWith(p));
  }

  /* Intercept window.fetch — covers modern code including ReCharge */
  const origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url);
      const isCart = isCartMutationUrl(url);
      const promise = origFetch.apply(this, arguments);
      if (isCart) {
        promise.then(() => syncCartCount()).catch(() => {});
      }
      return promise;
    };
  }

  /* Intercept XMLHttpRequest — covers older code and ReCharge's
     internal requests which sometimes use XHR */
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__pvdIsCart = isCartMutationUrl(url);
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__pvdIsCart) {
      this.addEventListener('load', () => syncCartCount());
    }
    return origSend.apply(this, arguments);
  };
})();
